const styles = (theme) => ({
  opcao: {
    display: "flex",
    alignItems: "center",
    gap: 0.75,
    width: "100%",
    minWidth: 0,
    // A lista é virtualizada e mede a altura de cada linha; um padding
    // previsível evita que a medição oscile entre opções.
    py: 0.5,
  },
  nome: {
    lineHeight: 1.4,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    minWidth: 0,
    flex: 1,
  },
  // Nome do cliente exibido dentro do campo, no lugar do texto do input.
  nomeNoCampo: {
    // O recuo à esquerda não vem daqui: ele espelha o padding do
    // .MuiAutocomplete-input em sxCampoCliente, que varia com o tamanho do
    // campo. Ver a classe `campo-cliente-nome`.
    flex: 1,
    // Um <input> não tem largura intrínseca vinda do valor — ela vem do
    // atributo size —, mas uma caixa de texto tem: sem `width: 0` a largura
    // máxima do nome entra no cálculo do container e o campo inteiro cresce
    // conforme o nome do cliente. Com 0, o elemento só ocupa o espaço que
    // sobra, e a largura do campo volta a ser a de antes.
    width: 0,
    minWidth: 0,
    overflow: "hidden",
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",

    color: theme.palette.text.primary,
    // Deixa o clique passar para o campo, que abre a lista e foca o input.
    pointerEvents: "none",
  },
  selo: {
    height: 18,
    minWidth: 32,

    flexShrink: 0,
    "& .MuiChip-label": { px: 0.75 },
    // O selo de grupo econômico traz ícone; no tamanho padrão ele estoura os
    // 18px de altura.
    "& .MuiChip-icon": { ml: 0.5, mr: -0.25 },
  },
});

/**
 * Estilos do campo de um autocomplete de cliente que exibe o selo à direita.
 *
 * Não depende do tema (é só estrutura), então vai como objeto pronto para o
 * `sx` do TextField.
 *
 * O MUI posiciona `.MuiAutocomplete-endAdornment` de forma absoluta e reserva
 * um padding fixo no input para ele. Com um chip ali dentro, o adorno cresce
 * para a esquerda por cima do texto. Tornando-o estático, ele vira um item de
 * flex de verdade: o input encolhe com reticências e o bloco selo + limpar +
 * abrir nunca é espremido.
 */
export const sxCampoCliente = {
  "& .MuiAutocomplete-inputRoot": {
    flexWrap: "nowrap",
    gap: 0.5,
    // O padding reservado para o adorno absoluto deixa de fazer sentido.
    paddingRight: "9px !important",
  },
  "& .MuiAutocomplete-endAdornment": {
    position: "static",
    // O MUI centraliza o adorno absoluto com top: 50% + translate(0, -50%).
    // O `top` morre junto com o position, mas o transform sobrevive e
    // deslocaria o bloco meia altura para cima.
    top: "auto",
    transform: "none",
    display: "flex",
    alignItems: "center",
    flexShrink: 0,
  },
  // O nome exibido no lugar do texto do input é um irmão do <input>, então não
  // herda o padding dele e ficaria colado na borda. Estes seletores repetem
  // exatamente o recuo que o MUI dá ao input de cada variante e tamanho, para
  // que o texto comece no mesmo ponto dos demais campos da tela.
  "& .MuiOutlinedInput-root .campo-cliente-nome": {
    paddingLeft: "5px",
  },
  "& .MuiOutlinedInput-root.MuiInputBase-sizeSmall .campo-cliente-nome": {
    paddingLeft: "8px",
  },
  // No variante filled o MUI usa `padding: 2.5px 4px` (small) e `7px 4px`
  // (normal) — 4px à esquerda nos dois casos, então uma regra basta.
  "& .MuiFilledInput-root .campo-cliente-nome": {
    paddingLeft: "4px",
  },
  "& .MuiAutocomplete-input": {
    textOverflow: "ellipsis",
    // O MUI já usa width: 0 + flexGrow: 1 aqui; o minWidth de 30px garante que
    // o campo não desapareça quando o nome é longo e o adorno, largo.
    minWidth: "30px",
  },
};

/**
 * Aplicado por cima do sxCampoCliente enquanto o nome do cliente é exibido
 * como elemento próprio (ver `CampoCliente`).
 *
 * Um <input> nativo não mantém reticências em foco: o navegador rola o texto
 * para acompanhar o cursor, e é isso que fazia o nome longo correr até o chip
 * ao clicar no campo. A saída é não deixar o nome dentro do input — enquanto
 * ninguém está digitando, ele vira um elemento de texto comum, que respeita
 * text-overflow em qualquer estado, e o input é colapsado.
 *
 * O input continua no DOM e focado, então digitar volta tudo ao normal.
 *
 * A chave do input repete o que o sxCampoCliente declara para ela: o spread
 * que junta os dois é raso, então este objeto substitui aquele por inteiro em
 * vez de completá-lo.
 */
export const sxCampoClienteComNome = {
  "& .MuiAutocomplete-input": {
    textOverflow: "ellipsis",
    width: "0 !important",
    minWidth: "0 !important",
    padding: "0 !important",
    // O MUI declara flexGrow: 1 para este mesmo seletor, com a mesma
    // especificidade — quem vence depende da ordem em que o emotion insere as
    // regras. Sem o !important o input poderia crescer e disputar espaço com o
    // nome, e as reticências apareceriam antes da hora.
    flexGrow: "0 !important",
  },
};

export default styles;
